/**
 * React Query hook for user preferences (i18n settings, etc.)
 * 
 * Fetches and updates user preferences from the backend API.
 * Used by the I18nProvider for timezone and language settings.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getStoredToken } from "../auth";
import type { UserPreferencesUpdate } from "@nordex/types";

import { API_BASE_URL } from "@/backend/config";

/** Response from GET /api/user/preferences */
export interface UserPreferencesResponse {
  language: string | null;
  timezone: string | null;
  use_utc: boolean | null;
  clock_format: '12h' | '24h' | null;
  default_leverage: Record<string, number> | null;
  order_confirmation_enabled: boolean | null;
  slippage_tolerance: number | null;
  chart_overlays: Record<string, boolean> | null;
}

/**
 * Hook for fetching user preferences from the backend.
 * 
 * @param userId - The user's ID (required to enable the query)
 * @returns Query result with user preferences data
 */
export const useUserPreferences = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["user-preferences", userId],
    queryFn: async (): Promise<UserPreferencesResponse> => {
      const token = getStoredToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/user/preferences`, {
        headers,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user preferences");
      }

      return response.json();
    },
    enabled: !!userId,
    staleTime: 60000, // 1 minute - preferences don't change often
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook for updating user preferences on the backend.
 * 
 * @param userId - The user's ID (for query invalidation)
 * @returns Mutation for updating preferences
 */
export const useUpdateUserPreferences = (userId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: UserPreferencesUpdate): Promise<UserPreferencesResponse> => {
      const token = getStoredToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/user/preferences`, {
        method: "PUT",
        headers,
        credentials: "include",
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error("Failed to update user preferences");
      }

      return response.json();
    },
    onSuccess: (data) => {
      // Update the cache with the new data
      queryClient.setQueryData(["user-preferences", userId], data);
    },
    onError: (error) => {
      console.error("[useUpdateUserPreferences] Failed to update preferences:", error);
    },
  });
};

/**
 * Combined hook that provides both query and mutation for user preferences.
 * 
 * @param userId - The user's ID
 * @returns Object with preferences data and update function
 */
export const useUserPreferencesWithMutation = (userId: string | undefined) => {
  const query = useUserPreferences(userId);
  const mutation = useUpdateUserPreferences(userId);

  return {
    // Query state
    preferences: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    
    // Mutation
    updatePreferences: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
  };
};
